class BundleDrawer {
  constructor(config) {
    this.variantId = config.variantId;
    this.optionsToChoose = config.optionsToChoose;
    this.products = config.products;
    this.selectedVariants = [];
    this.isOpen = false;
    this.triggerButton = null;

    this.overlay = document.getElementById('bundleDrawerOverlay');
    this.drawer = document.getElementById('bundleDrawer');
    this.closeBtns = this.drawer ? this.drawer.querySelectorAll('#bundleDrawerClose') : null;
    this.productItems = this.drawer ? this.drawer.querySelectorAll('.bundle-product-item') : [];
    this.totalElement = this.drawer ? this.drawer.querySelector('#bundleTotal') : null;
    this.addToBagBtn = this.drawer ? this.drawer.querySelector('#bundleAddToBag') : null;
    this.helperTextElement = this.drawer ? this.drawer.querySelector('#bundleHelperText') : null;
    this.submitButton = document.getElementById('ProductSubmitButton');
    this.submitButtonsSticky = document.querySelectorAll('.button-sticky-bar');

    // Create aria-live region for bundle updates
    if (this.drawer) {
      this.ariaLiveRegion = document.createElement('div');
      this.ariaLiveRegion.setAttribute('aria-live', 'polite');
      this.ariaLiveRegion.setAttribute('aria-atomic', 'true');
      this.ariaLiveRegion.className = 'sr-only';
      this.drawer.appendChild(this.ariaLiveRegion);
    }

    this.init();
  }
  
  init() {
    // Close button handlers
    if (this.closeBtns.length > 0) {
      this.closeBtns.forEach(btn => {
        btn.addEventListener('click', () => this.toggle());
      });
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.toggle());
    }
    
    // Product selection handlers
    this.productItems.forEach(item => {
      item.addEventListener('click', () => {
        const variantId = item.dataset.variantId;
        const price = parseInt(item.dataset.price);
        if (variantId && !isNaN(price)) {
          this.selectVariant(variantId, price);
        }
      });
    });
    
    // Add to bag handler
    if (this.addToBagBtn) {
      this.addToBagBtn.addEventListener('click', () => this.handleAddToCart());
    }
    
    // Submit button handler for bundle products
    if (this.submitButton && this.submitButton.dataset.bundleProduct === 'true') {
      this.submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    }

    this.submitButtonsSticky.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Initialize button state
    this.updateAddToCartButton();
    this.updateTotal();
    this.updateHelperText();
    this.updateProductStates();
  }
  
  toggle() {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      // Store trigger button for focus restoration
      this.triggerButton = document.activeElement;

      this.overlay.classList.remove('bundle-overlay--hidden');
      this.overlay.classList.add('bundle-overlay--visible');
      this.drawer.classList.remove('bundle-drawer--closed');
      this.drawer.classList.add('bundle-drawer--open');
      document.body.classList.add('overlay-open');
      // Global function to lock body scroll
      lockBodyScroll();

      // Move focus to first product item
      setTimeout(() => {
        if (this.productItems.length > 0) {
          this.productItems[0].focus();
        }
      }, 100);

      // Announce to screen readers
      if (this.ariaLiveRegion) {
        this.ariaLiveRegion.textContent = `Bundle drawer opened. Select ${this.optionsToChoose} products to continue.`;
      }
    } else {
      this.overlay.classList.add('bundle-overlay--hidden');
      this.overlay.classList.remove('bundle-overlay--visible');
      this.drawer.classList.add('bundle-drawer--closed');
      this.drawer.classList.remove('bundle-drawer--open');
      document.body.classList.remove('overlay-open');
      // Global function to unlock body scroll
      unlockBodyScroll();

      // Restore focus to trigger button
      if (this.triggerButton) {
        this.triggerButton.focus();
      }

      // Clear aria-live message
      if (this.ariaLiveRegion) {
        this.ariaLiveRegion.textContent = '';
      }
    }
  }
  
  selectVariant(variantId, price) {
    const existingIndex = this.selectedVariants.findIndex(v => v.id === variantId);
    const productItem = Array.from(this.productItems).find(item => item.dataset.variantId === variantId);
    const productName = productItem ? productItem.textContent.trim() : 'Product';

    if (existingIndex > -1) {
      // Deselect - always allow deselection
      this.selectedVariants.splice(existingIndex, 1);
      this.updateProductItemSelection(variantId, false);

      // Announce deselection
      if (this.ariaLiveRegion) {
        this.ariaLiveRegion.textContent = `${productName} removed from selection. ${this.selectedVariants.length} of ${this.optionsToChoose} selected.`;
      }
    } else {
      // Select - only if under the limit
      if (this.selectedVariants.length < this.optionsToChoose) {
        this.selectedVariants.push({ id: variantId, price: price });
        this.updateProductItemSelection(variantId, true);

        // Announce selection
        if (this.ariaLiveRegion) {
          this.ariaLiveRegion.textContent = `${productName} added to selection. ${this.selectedVariants.length} of ${this.optionsToChoose} selected.`;
        }
      } else {
        // Already at max capacity, announce limit reached
        if (this.ariaLiveRegion) {
          this.ariaLiveRegion.textContent = `Maximum selection reached. Deselect an item first to select another.`;
        }
        return;
      }
    }

    this.updateTotal();
    this.updateAddToCartButton();
    this.updateHelperText();
    this.updateProductStates();
  }
  
  updateProductItemSelection(variantId, isSelected) {
    if (!this.drawer) {
      console.error('Bundle drawer not found');
      return;
    }
    
    const item = this.drawer.querySelector(`.bundle-product-item[data-variant-id="${variantId}"]`);
    if (!item) {
      console.error(`Product item not found for variant ID: ${variantId}`);
      return;
    }
    
    const radio = item.querySelector('.bundle-radio');
    const dot = item.querySelector('.bundle-radio__dot');
    
    if (!radio) {
      console.error('Radio element not found in product item');
      return;
    }
    
    if (!dot) {
      console.error('Radio dot element not found in product item');
      return;
    }
    
    if (isSelected) {
      item.classList.add('bundle-product-item--selected');
      radio.classList.add('bundle-radio--selected');
      dot.classList.remove('bundle-radio__dot--hidden');
      dot.classList.add('bundle-radio__dot--visible');
    } else {
      item.classList.remove('bundle-product-item--selected');
      radio.classList.remove('bundle-radio--selected');
      dot.classList.add('bundle-radio__dot--hidden');
      dot.classList.remove('bundle-radio__dot--visible');
    }
  }
  
  isSelected(variantId) {
    return this.selectedVariants.some(v => v.id === variantId);
  }
  
  updateTotal() {
    const total = this.selectedVariants.reduce((sum, variant) => sum + variant.price, 0);
    this.totalElement.textContent = this.formatPrice(total);
  }
  
  updateAddToCartButton() {
    if (this.selectedVariants.length === this.optionsToChoose) {
      this.addToBagBtn.classList.remove('bundle-drawer__add-button--disabled');
      this.addToBagBtn.classList.add('bundle-drawer__add-button--enabled');
      this.addToBagBtn.disabled = false;
    } else {
      this.addToBagBtn.classList.add('bundle-drawer__add-button--disabled');
      this.addToBagBtn.classList.remove('bundle-drawer__add-button--enabled');
      this.addToBagBtn.disabled = true;
    }
  }

  updateHelperText() {
    if (!this.helperTextElement) return;

    const selectedCount = this.selectedVariants.length;

    if (selectedCount === 0) {
      this.helperTextElement.textContent = `Select ${this.optionsToChoose} products to continue.`;
    } else {
      this.helperTextElement.textContent = `${selectedCount} of ${this.optionsToChoose} selected`;
    }
  }

  updateProductStates() {
    const isAtLimit = this.selectedVariants.length >= this.optionsToChoose;

    this.productItems.forEach(item => {
      const variantId = item.dataset.variantId;
      const isSelected = this.isSelected(variantId);

      // If we're at the limit and this item is not selected, disable it
      if (isAtLimit && !isSelected) {
        item.classList.add('bundle-product-item--disabled');
      } else {
        item.classList.remove('bundle-product-item--disabled');
      }
    });
  }

  formatPrice(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
  
  choiceBundleATC(variantId, components) {
    const bundleGroupId = new Date().getTime();
    const items = components.map(component => ({
      id: component.id,
      quantity: 1,
      properties: {
        '_bundleVariantId': variantId,
        '_bundleGroupId': bundleGroupId
      }
    }));

    this.addToCart({ items });
  }

  async addToCart(cartData) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (response.ok) {
        const responseData = await response.json();

        // Dispatch global cart update event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-drawer',
            items: cartData.items,
            cartData: responseData,
          });
        }

        // Update cart count or trigger cart drawer
        this.toggle();
        window.location.reload(); // Simple refresh, can be improved with cart drawer
      } else {
        console.error('Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async handleAddToCart() {
    // Double check we have exactly the right number of products
    if (this.selectedVariants.length !== this.optionsToChoose) {
      console.warn(`Must select exactly ${this.optionsToChoose} products. Currently selected: ${this.selectedVariants.length}`);
      return;
    }
    
    // Disable button during processing
    this.addToBagBtn.disabled = true;
    this.addToBagBtn.textContent = 'Adding...';
    
    try {
      this.choiceBundleATC(this.variantId, this.selectedVariants);
    } catch (error) {
      console.error('Error during bundle add to cart:', error);
      // Re-enable button on error
      this.addToBagBtn.disabled = false;
      this.addToBagBtn.textContent = 'Add to bag';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const bundleConfig = window.bundleConfig;
  if (bundleConfig && document.getElementById('bundleDrawer')) {
    window.bundleDrawer = new BundleDrawer(bundleConfig);
  }
});

// Global function to open drawer (backward compatibility)
function openBundleDrawer() {
  if (window.bundleDrawer) {
    window.bundleDrawer.toggle();
  }
}